import React from 'react';
import ReactDOM from 'react-dom/client';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { ConfigProvider, message } from 'antd';
import { EErrorStatus, errorMapper } from '@axmit/error-helper';
import { envConfig } from 'common/config/env.config';
import { ThirdPartyServicesHelper } from 'common/helpers/ThirdPartyServicesHelper.helpers';
import { storageHelper } from 'common/helpers/storage.helper';
import { configureJwtInterceptors } from 'common/axios/jwt';
import { history, store } from 'app/store';
import 'app/assets/sass/index.scss';
import { App } from 'app/App';
import { ECustomerPhoneErrorCode, ECustomerPhoneErrorMessage } from 'entities/Customer/Customer.models';
import { EBaseErrorMessage } from 'entities/Auth/Auth.models';
import { ShopBag } from 'entities/Orders/components/ShopBag';

// Check access to localStorage (used by other parts of the app)
storageHelper.checkAndCreateLocalStorage();

// Axios initialization
export const axiosConfig = {
  axios,
  refreshTokenEndpoint: '/auth',
};

// Sentry initialization
Sentry.init({
  dsn: envConfig.SENTRY_DSN || envConfig.VITE_SENTRY_DSN,
  environment: envConfig.NODE_ENV,
  ignoreErrors: ['ResizeObserver loop limit exceeded'],
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 1.0,
});

// @ts-ignore
window.checkSentry = (text?: string) => {
  const msg = `Sentry successfully connected. Config: ${JSON.stringify(envConfig)} ${new Date()}. Test string: ${text}`;
  console.log('Go to slack channel and check an error with message: ');
  console.log(msg);
  Sentry.captureException(msg);
};

ThirdPartyServicesHelper.initializeGoogleTag(envConfig.GOOGLE_TAG_ID || envConfig.VITE_GOOGLE_TAG_ID, envConfig.NODE_ENV);
ThirdPartyServicesHelper.initializeMetaPixel(envConfig.META_PIXEL_ID || envConfig.VITE_META_PIXEL_ID, envConfig.NODE_ENV);
ThirdPartyServicesHelper.initializeTikTokPixel(envConfig.TIKTOK_PIXEL_ID || envConfig.VITE_TIKTOK_PIXEL_ID, envConfig.NODE_ENV);

configureJwtInterceptors(axiosConfig);

axios.defaults.baseURL = `/api`;
axios.interceptors.request.use((config) => {
  config.headers['Source-Service'] = 'customer';
  config.headers['x-client-version'] = envConfig.RELEASE_VERSION || envConfig.VITE_RELEASE_VERSION || '';
  config.headers['x-client-id'] = ThirdPartyServicesHelper.getGA4ClientIdFromCookie();
  return config;
});
axios.interceptors.response.use(
  (response) => response.data,
  (error) =>
    errorMapper(
      error,
      { showError: message.error, defaultError: EBaseErrorMessage.Default },
      {
        withPopupMessage: [
          EErrorStatus.BadRequest,
          EErrorStatus.Conflict,
          EErrorStatus.PayloadTooLarge,
          EErrorStatus.FailedDependency,
          EErrorStatus.TooManyRequests,
          EErrorStatus.RetryWith,
          EErrorStatus.InternalServerError,
        ],
      },
      (msg: [string, string] | string) => {
        if (Array.isArray(msg)) {
          const [code, text] = msg;

          if (code === ECustomerPhoneErrorCode.NotUnique) {
            return ECustomerPhoneErrorMessage.NotUnique;
          }
          return text;
        }

        return msg;
      },
    ),
);

// Render Setup
const MOUNT_NODE = document.getElementById('root')!;

ReactDOM.createRoot(MOUNT_NODE).render(
  <Provider store={store}>
    <HistoryRouter history={history as any}>
      <ConfigProvider
        theme={{
          token: {
            fontFamily: "'Uniform Rounded', 'sans-serif'",
          },
        }}
      >
        <ShopBag />
        <App />
      </ConfigProvider>
    </HistoryRouter>
  </Provider>,
);
